import { supabase } from '@/lib/supabase';

const STORAGE_KEYS = {
  GAME_PROGRESS: 'buzzy_game_progress',
  SOUND_PREFERENCE: 'buzzy_sound_preference',
  AUTO_SUBMIT_PREFERENCE: 'buzzy_auto_submit_preference',
} as const;

interface LocalProgress {
  gradeLevel?: string | number;
  totalAttempts?: number;
  correctAnswers?: number;
  currentStreak?: number;
  bestStreak?: number;
  sessionsPlayed?: number;
  lastUpdated?: string;
}

interface LocalSettings {
  voice_enabled: boolean;
  auto_submit: boolean;
}

interface ExtractionResult {
  hasData: boolean;
  progress: LocalProgress | null;
  settings: LocalSettings;
}

function extractLocalStorageData(username: string): ExtractionResult {
  const allProgress = JSON.parse(localStorage.getItem(STORAGE_KEYS.GAME_PROGRESS) || '{}');
  const allSound = JSON.parse(localStorage.getItem(STORAGE_KEYS.SOUND_PREFERENCE) || '{}');
  const allAutoSubmit = JSON.parse(localStorage.getItem(STORAGE_KEYS.AUTO_SUBMIT_PREFERENCE) || '{}');

  const progress = allProgress[username] || null;
  const settings = {
    voice_enabled: typeof allSound[username] === 'boolean' ? allSound[username] : true,
    auto_submit: typeof allAutoSubmit[username] === 'boolean' ? allAutoSubmit[username] : true,
  };

  return {
    hasData: Boolean(progress),
    progress,
    settings,
  };
}

function clearLocalStorageData(username: string): void {
  const keysToClean = [
    STORAGE_KEYS.GAME_PROGRESS,
    STORAGE_KEYS.SOUND_PREFERENCE,
    STORAGE_KEYS.AUTO_SUBMIT_PREFERENCE,
  ] as const;

  keysToClean.forEach((key) => {
    const data = JSON.parse(localStorage.getItem(key) || '{}');
    if (data[username]) {
      delete data[username];
      localStorage.setItem(key, JSON.stringify(data));
    }
  });
}

function mapProgress(userId: string, progress: LocalProgress) {
  const totalAttempts = progress.totalAttempts || 0;
  const correctAnswers = progress.correctAnswers || 0;
  
  return {
    user_id: userId,
    grade_level: Number(progress.gradeLevel) || 0,
    words_attempted: totalAttempts,
    words_correct: correctAnswers,
    words_incorrect: Math.max(totalAttempts - correctAnswers, 0),
    current_streak: progress.currentStreak || 0,
    longest_streak: progress.bestStreak || 0,
    total_sessions: progress.sessionsPlayed || 0,
    updated_at: progress.lastUpdated || new Date().toISOString(),
  };
}

export interface MigrationParams {
  userId: string;
  username: string;
}

export interface MigrationResult {
  success: boolean;
  skipped?: boolean;
  alreadyMigrated?: boolean;
  noDataToMigrate?: boolean;
  error?: any;
}

export async function migrateLocalDataToCloud({ userId, username }: MigrationParams): Promise<MigrationResult> {
  if (!supabase || !userId || !username) {
    return { success: false, skipped: true };
  }

  const { data: profile, error: profileError } = await supabase
    .from('user_profiles')
    .select('migrated_from_local')
    .eq('id', userId)
    .maybeSingle();

  if (profileError) {
    return { success: false, error: profileError };
  }

  if (profile?.migrated_from_local) {
    return { success: true, alreadyMigrated: true };
  }

  const localData = extractLocalStorageData(username);
  if (!localData.hasData) {
    return { success: true, noDataToMigrate: true };
  }

  try {
    if (localData.progress) {
      await supabase.from('user_progress').upsert(mapProgress(userId, localData.progress), {
        onConflict: 'user_id,grade_level',
      });
    }

    await supabase.from('user_settings').upsert({
      user_id: userId,
      voice_enabled: localData.settings.voice_enabled,
      auto_submit: localData.settings.auto_submit,
    });

    await supabase
      .from('user_profiles')
      .update({
        migrated_from_local: true,
        migration_date: new Date().toISOString(),
      })
      .eq('id', userId);

    clearLocalStorageData(username);

    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
}
